/**
 * TODO - preamble
 */
import { logger } from "@jslib/shared/utils/logger";

import {
  type CollectFieldsResponse,
  type DatabaseId,
  type ViewModel,
  type VerificationResponse,
  type VerificationStep,
  type ProgramTheme,
  type DocUploadViewModel,
  type DocUploadRequest,
  type DocUploadResponse,
  type EmailLoopResponse,
  type ErrorResponse,
  type Locale,
  type NavigatorBeta,
  type NetworkErrorId,
  type NewVerificationRequest,
  type ApplicableDocTypesResponse,
  type MockStep,
  type SSOResponse,
  type SocialSecurityResponse,
  type CompleteAuthenticationViewModel,
  type CompleteAuthenticationResponse,
  type AllStatuses,
} from "../types/types";
import { VerificationStepsEnum } from "../types/runtimeTypes";
import {
  assertValidLocale,
  assertValidProgramId,
  assertValidVerificationStepName,
} from "../types/assertions";
import { getVerificationIdFromQueryString } from "../utils/routing/Url";
import { getOptions } from "../../options/options";
import { GetEmptyTheme } from "../types/empties";
import { resolveTrackingId } from "../conversion/conversion";
import { getLocaleSafely } from "../intl/intl";
import { EventName, recordGaEvent } from "../GoogleAnalytics/ga";

import { fetchExistingVerificationRequest } from "./VerificationApiClient/fetchExistingVerificationRequest";
import { PostJson, GetJson, DeleteJson, GetResponse, PostFiles } from "./Network";
import { getRouteOverride, getOverriddenMock } from "./TestingRouteOverrides";
import {
  getDocTypeForCountriesUrl,
  getEffectiveSubmissionUrl,
  getFieldsToCollectUrl,
  getInstallPageUrl,
  getNewEmailCodeResendUrl,
  getNewSmsCodeResendUrl,
  getNewVerificationRequestUrl,
  getProgramThemeUrl,
  getPublishedVersion,
} from "./ApiUrls";
import { buildRequestBody } from "./StepToRequest";
import { getClientParams } from "./clientParams";

/**
 * Initiate a new verification attempt with the SheerID REST service.
 *
 * @param programId Your programId from my.sheerid.com
 * @param trackingId Conversion trackingId to associate with this verification attempt.
 */
async function fetchNewVerificationRequest(
  programId: DatabaseId,
  trackingId: string = undefined,
  forceNewVerificationRequest = false,
): Promise<VerificationResponse> {
  const resolvedTrackingId = resolveTrackingId(trackingId);

  try {
    let response: VerificationResponse;

    assertValidProgramId(programId);

    const windowQueryString = window.location.search;
    let verificationId;

    if (!forceNewVerificationRequest) {
      verificationId =
        getOptions().verificationId || getVerificationIdFromQueryString(windowQueryString);
    }

    if (verificationId) {
      logger.info("fetchNewVerificationRequest: Calling for existing verification request");
      response = await fetchExistingVerificationRequest(verificationId);
      if (!response) {
        const networkErrorId: NetworkErrorId = "failedToFetch";
        throw new Error(networkErrorId);
      }
    }

    if (
      !verificationId ||
      (response.currentStep === VerificationStepsEnum.error &&
        response.errorIds.includes("noVerification"))
    ) {
      const url: string = getNewVerificationRequestUrl();
      const requestBody: NewVerificationRequest = {
        programId,
      };

      if (!(window.navigator as NavigatorBeta).globalPrivacyControl && resolvedTrackingId) {
        logger.info(`Adding trackingId ${resolvedTrackingId}`);
        requestBody.trackingId = resolvedTrackingId;
      }

      const installPageUrl = getInstallPageUrl();
      if (installPageUrl) {
        logger.info(`Adding installPageUrl ${installPageUrl}`);
        requestBody.installPageUrl = installPageUrl;
      }

      response = (await PostJson(url, requestBody, {
        ...getClientParams(),
        jslibVersion: getPublishedVersion(), // deprecated: superseded by ClientVersion
      })) as VerificationResponse;

      // This is a new verification response it will confidently declare it's
      // locale is `en-US` In most cases we want the verification response's
      // locale to win to respect the locale a users is verifying in. For new
      // verifications we want to use the best locale NOT including the
      // verification response. So we set the response's locale to that so we
      // can treat it as correct from here out.
      response.locale = getLocaleSafely();
    }

    const overriddenStep: MockStep = getRouteOverride(response);
    if (overriddenStep) {
      const mockResponse: VerificationResponse = await getOverriddenMock(overriddenStep, response);

      if (mockResponse) {
        // Reset the mocked locale to the best non-response locale when loading a
        // mock. This ensures that we load the correct theme/messages.
        mockResponse.locale = getLocaleSafely();
        return mockResponse;
      }
    }

    return response;
  } catch (e) {
    logger.error(e, "fetchNewVerificationRequest");
    throw e;
  }
}

async function fetchProgramTheme(programId: DatabaseId, locale?: Locale): Promise<ProgramTheme> {
  if (!getOptions().doFetchTheme) {
    return GetEmptyTheme();
  }
  logger.info("fetchProgramTheme: Calling back-end for program theme");

  try {
    assertValidProgramId(programId);

    if (locale) assertValidLocale(locale);
    const cleanLocale = locale || getLocaleSafely();

    return (await GetJson(
      getProgramThemeUrl(programId, cleanLocale),
      getClientParams(),
    )) as ProgramTheme;
  } catch (e) {
    logger.error(e, "fetchProgramTheme");
    throw e;
  }
}

async function getResendNewSmsCode(verificationId: DatabaseId): Promise<any> {
  logger.info("Resending new sms code");

  try {
    const url = getNewSmsCodeResendUrl(verificationId);
    const response = await GetResponse(url, getClientParams());
    return response;
  } catch (e) {
    logger.error(e, "getResendNewSmsCode");
  }
}

async function getResendNewEmailCode(verificationId: DatabaseId): Promise<any> {
  logger.info("Resending new email code");

  try {
    const url = getNewEmailCodeResendUrl(verificationId);
    const response = await GetResponse(url, getClientParams());
    return response;
  } catch (e) {
    logger.error(e, "getResendNewEmailCode");
  }
}

export function getMockVerificationRequestErrorResponse(
  errorId: NetworkErrorId,
): VerificationResponse {
  return {
    verificationId: null,
    currentStep: "error",
    errorIds: [errorId],
    segment: null,
    subSegment: null,
    locale: "en-US",
  };
}

async function submitFromVerificationStep(
  step: VerificationStep,
  previousResponse: VerificationResponse,
  viewModel: ViewModel,
): Promise<VerificationResponse | ErrorResponse> {
  let response;
  const {
    docUpload,
    cancelSocialSecurityNumber,
    sso,
    cancelEmailLoop,
    cancelDocUpload,
    completeAuthentication,
  } = VerificationStepsEnum;

  try {
    performance.clearMarks(EventName.STEP_SUBMITTED); // make sure we measure _this_ attempt
    performance.mark(EventName.STEP_SUBMITTED);
    recordGaEvent({
      eventName: EventName.STEP_SUBMITTED,
      params: {
        step,
        eventDuration: performance.measure(
          EventName.STEP_SUBMITTED,
          EventName.STEP_SEEN,
          EventName.STEP_SUBMITTED,
        ).duration,
      },
    });
  } catch (e) {
    logger.error(e);
  }

  try {
    if (step === docUpload) {
      const requestBody: DocUploadRequest = viewModel as DocUploadViewModel;
      const { file1, file2, file3 } = requestBody;
      response = await PostFiles(
        (previousResponse as DocUploadResponse).submissionUrl,
        [file1, file2, file3],
        getClientParams(),
      );
    }
    if (step === cancelSocialSecurityNumber || step === sso) {
      response = await DeleteJson(
        (previousResponse as SSOResponse | SocialSecurityResponse).cancelUrl,
        getClientParams(),
      );
    }
    if (step === cancelEmailLoop) {
      response = await DeleteJson(
        (previousResponse as EmailLoopResponse).cancelUrl,
        getClientParams(),
      );
    }
    if (step === cancelDocUpload) {
      response = await DeleteJson(
        (previousResponse as DocUploadResponse).submissionUrl,
        getClientParams(),
      );
    }
    if (
      step === completeAuthentication &&
      (viewModel as CompleteAuthenticationViewModel).didManuallyVerify
    ) {
      response = await DeleteJson(
        (previousResponse as CompleteAuthenticationResponse).cancelUrl,
        getClientParams(),
      );
    }

    if (!response) {
      const requestBody = buildRequestBody(step, viewModel, previousResponse);

      response = await PostJson(
        getEffectiveSubmissionUrl(step, previousResponse, previousResponse.verificationId),
        requestBody,
        getClientParams(),
      );
    }

    try {
      performance.clearMarks(EventName.STEP_ACCEPTED); // make sure we measure _this_ attempt
      performance.mark(EventName.STEP_ACCEPTED);
      recordGaEvent({
        eventName: EventName.STEP_ACCEPTED,
        params: {
          step,
          eventDuration: performance.measure(
            EventName.STEP_ACCEPTED,
            EventName.STEP_SEEN,
            EventName.STEP_ACCEPTED,
          ).duration,
        },
      });
    } catch (e) {
      logger.error(e);
    }

    return response;
  } catch (e) {
    logger.error(e, "submitFromVerificationStep");
    // At least show the error step...
    return getUnrecoverableErrorStep();
  }
}

function getUnrecoverableErrorStep(): ErrorResponse {
  // TODO build an empty error step response with "unknown error" or some other appropriate error id.
  const response: ErrorResponse = {
    verificationId: "",
    currentStep: "error",
    segment: null,
    subSegment: null,
    redirectUrl: null,
    locale: "en-US",
    errorIds: ["unknownError"],
  };
  return response;
}

async function submitStep(
  stepName: VerificationStep,
  previousResponse: VerificationResponse,
  viewModel: ViewModel,
): Promise<VerificationResponse | ErrorResponse> {
  assertValidVerificationStepName(stepName);
  if (VerificationStepsEnum[stepName]) {
    return submitFromVerificationStep(stepName, previousResponse, viewModel);
  }
  return Promise.reject(new Error(`Unknown step ${stepName}`));
}

export async function getAllFieldsToCollect(
  verificationId: DatabaseId,
  step: VerificationStep,
  // This is just a partial typing of the valid request body that covers current
  // usage. It was previously typed as a partial viewmodel which is incorrect
  // and let broken code through. Expand (correctly) as needed.
  requestBody: { statuses?: AllStatuses[] },
): Promise<CollectFieldsResponse> {
  if (VerificationStepsEnum[step]) {
    const response = (await PostJson(
      getFieldsToCollectUrl(verificationId, step),
      requestBody,
      getClientParams(),
    )) as VerificationResponse;
    if (response.currentStep === "error") {
      Promise.reject(new Error(`Failed to determine fields to collect: ${response}`));
    }
    return response as CollectFieldsResponse;
  }
  return Promise.reject(new Error(`Unknown step ${step}`));
}

const getIdCheckDocTypeForCountry = async (
  verificationId: DatabaseId,
  countries: string[],
): Promise<ApplicableDocTypesResponse> =>
  GetJson(
    getDocTypeForCountriesUrl(verificationId, countries),
    getClientParams(),
  ) as Promise<ApplicableDocTypesResponse>;

export const VerificationApiClient = {
  fetchNewVerificationRequest,
  fetchExistingVerificationRequest,
  fetchProgramTheme,
  getResendNewSmsCode,
  getResendNewEmailCode,
  getIdCheckDocTypeForCountry,
  submitStep,
};
